// errorReport.ts — lightweight, dependency-free crash reporting to Sentry's ingest
// API. LIVE: reports to the ReNurture Sentry project below. A Sentry DSN is a public
// ingest key (it ships in client JS by design), so it's safe to inline; override per
// environment with VITE_SENTRY_DSN if ever needed. Captures unhandled errors and
// promise rejections; call reportError() manually from catch blocks too.
const DEFAULT_DSN = 'https://c140600953f5249c491909c93e0575c8@o4511585295269888.ingest.de.sentry.io/4511585311326288';
const DSN = (import.meta.env.VITE_SENTRY_DSN as string) || DEFAULT_DSN;
const RELEASE = (import.meta.env.VITE_APP_VERSION as string) || 'renurture-app';

function parseDsn(dsn: string): { key: string; host: string; projectId: string } | null {
  const m = /^https:\/\/([^@]+)@([^/]+)\/(.+)$/.exec(dsn);
  return m ? { key: m[1], host: m[2], projectId: m[3] } : null;
}
const hex = (n: number) =>
  Array.from(crypto.getRandomValues(new Uint8Array(n)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

let lastSent = 0;

/** Send one error to Sentry (no-op unless a DSN is configured). Throttled so a tight
 *  error loop can't flood the endpoint. */
export function reportError(err: unknown, tags: Record<string, string> = {}): void {
  const d = parseDsn(DSN);
  if (!d) return;
  const now = Date.now();
  if (now - lastSent < 1000) return; // crude storm guard
  lastSent = now;
  const e = err as { name?: string; message?: string; stack?: string } | undefined;
  const id = hex(16);
  const event = {
    event_id: id,
    timestamp: now / 1000,
    platform: 'javascript',
    level: 'error',
    release: RELEASE,
    environment: 'production',
    exception: { values: [{ type: e?.name || 'Error', value: String(e?.message ?? err) }] },
    extra: { stack: e?.stack || null },
    tags: { surface: 'app', ...tags },
  };
  const body =
    JSON.stringify({ event_id: id, sent_at: new Date().toISOString() }) + '\n' +
    JSON.stringify({ type: 'event' }) + '\n' +
    JSON.stringify(event) + '\n';
  const url = `https://${d.host}/api/${d.projectId}/envelope/?sentry_key=${d.key}&sentry_version=7`;
  // keepalive so the report still goes out during a crash / page unload.
  fetch(url, { method: 'POST', headers: { 'content-type': 'application/x-sentry-envelope' }, body, keepalive: true }).catch(() => {});
}

/** Install global handlers. Safe to call always — it self-disables without a DSN. */
export function installErrorReporting(): void {
  if (!parseDsn(DSN)) return;
  window.addEventListener('error', (ev) => reportError(ev.error || ev.message, { kind: 'onerror' }));
  window.addEventListener('unhandledrejection', (ev) => reportError((ev as PromiseRejectionEvent).reason, { kind: 'unhandledrejection' }));
}
